import { cn } from "@/lib/cn";

/**
 * LEVEL 2 — the flat page section.
 *
 * FixControl's default layout unit used to be a card: `SectionShell` painted
 * `card p-4`, `Panel` painted `card p-5`, and a settings page was six of them
 * stacked. Nothing on those pages was a discrete object — they were headings
 * with fields under them — so every border was structure the content did not
 * have, and anything genuinely boxed (a table, a status block, a real object
 * tile) ended up as a box inside a box.
 *
 * This is the replacement. It is still a container — removing them outright
 * put flat sections next to surviving cards in one scroll column, which read
 * worse than the uniform noise it replaced — but a QUIET one: outline only,
 * no fill, 6px radius, a border the same weight as a table row separator.
 *
 * What the card era actually got wrong was not the box, it was the volume and
 * the stacking. So the box stays and the nesting goes: inside a section,
 * tables, rows, lists and status blocks stay flat, and the page is one level
 * deep instead of three.
 *
 * The surface ladder this belongs to:
 *
 *   LEVEL 1  page      `<OperatorPage>`   — background only, never a card
 *   LEVEL 2  section   `<PageSection>`    — this: type + space, no border
 *   LEVEL 3  object    `.card`            — a thing you can select or act on
 *   LEVEL 4  status    `<StatusPanel>`    — a tinted surface that means something
 *   LEVEL 5  control   Button/Input/…     — keeps its own boundary
 *
 * See `docs/ui-surfaces.md` for when each one is the right answer.
 */
export function PageSection({
  id,
  title,
  description,
  actions,
  /**
   * Heading level. A section directly under the page `<h1>` is an `h2` (the
   * default); a sub-section inside another section is an `h3`. Getting this
   * right is the whole reason removing the cards does not cost the page its
   * structure — the outline a screen reader reads is the headings, and it
   * has to keep matching what the eye sees.
   */
  level = 2,
  /**
   * Drop the container and render the heading + content straight onto the
   * page. For the handful of blocks that are genuinely not a section of
   * their own — a single note under another section, a block already
   * inside a card.
   */
  bare = false,
  /**
   * Caps the content column. Off by default: a settings page that stopped
   * two thirds of the way across the window, next to operator pages that
   * use all of it, read as a broken layout rather than as a deliberate
   * measure. Long prose still caps itself — the section's own description
   * is capped at 70ch — and individual inputs keep their own widths.
   */
  maxWidth,
  children,
  className,
  headerClassName,
  "data-testid": testId,
}: {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  level?: 2 | 3;
  bare?: boolean;
  maxWidth?: "prose" | "form" | "none";
  children?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  "data-testid"?: string;
}) {
  const Heading = level === 3 ? "h3" : "h2";
  const hasHeader = Boolean(title || description || actions);
  return (
    <section
      id={id}
      data-testid={testId}
      className={cn(
        // `scroll-mt` so an in-page anchor lands under the sticky page bar
        // rather than behind it — the cards used to carry this themselves.
        "scroll-mt-24",
        // `ui-section` is the quiet container itself — see globals.css.
        !bare && "ui-section p-4",
        maxWidth === "prose" && "max-w-[70ch]",
        maxWidth === "form" && "max-w-2xl",
        className,
      )}
    >
      {hasHeader && (
        <header
          className={cn(
            // Below `sm` the heading takes the row and the action drops under
            // it: side by side at 390px a wide action squeezes the title to a
            // few characters and the description to one word per line.
            "flex flex-wrap sm:flex-nowrap items-start justify-between gap-2 sm:gap-3",
            children ? "mb-3" : undefined,
            headerClassName,
          )}
        >
          <div className="min-w-0 basis-full sm:basis-auto">
            {title && (
              <Heading
                // Named so a `<fieldset>` (or any grouping wrapper) can point
                // `aria-labelledby` at it instead of repeating the string.
                id={id ? `${id}-heading` : undefined}
                className="text-[14px] font-semibold text-fg-bright tracking-tight"
              >
                {title}
              </Heading>
            )}
            {description && (
              <p className="text-[12.5px] leading-relaxed text-fg-dim mt-1 max-w-[70ch]">
                {description}
              </p>
            )}
          </div>
          {actions && (
            <div className="shrink-0 flex items-center gap-1.5 flex-wrap">{actions}</div>
          )}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * A form section: `PageSection` with the field rhythm and the readable
 * column width already set, so a settings page is a document of labelled
 * fields rather than a column of panels.
 *
 * `<fieldset>`/`<legend>` is deliberately NOT used by default. The heading
 * is the group's name and `aria-labelledby` ties the fields to it; a legend
 * would announce the same string twice. Pass `group` for the cases that are
 * genuinely a set of related controls (radio groups, permission matrices),
 * where the grouping is the semantics rather than the layout.
 */
export function FormSection({
  id,
  title,
  description,
  actions,
  level = 2,
  bare = false,
  group = false,
  maxWidth,
  children,
  className,
  "data-testid": testId,
}: {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  level?: 2 | 3;
  bare?: boolean;
  maxWidth?: "prose" | "form" | "none";
  /** Wrap the fields in a `<fieldset>` labelled by the heading. */
  group?: boolean;
  children?: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  const body = <div className="space-y-4">{children}</div>;
  return (
    <PageSection
      id={id}
      title={title}
      description={description}
      actions={actions}
      level={level}
      bare={bare}
      maxWidth={maxWidth}
      className={className}
      data-testid={testId}
    >
      {group && id ? (
        <fieldset aria-labelledby={`${id}-heading`} className="min-w-0">
          {body}
        </fieldset>
      ) : (
        body
      )}
    </PageSection>
  );
}
